import React, { useState } from 'react';
import { useLocale } from '../contexts/LocaleContext';
import { useNotifications } from '../contexts/NotificationsContext';
import { useSettings } from '../contexts/SettingsContext';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from './ui/sidebar';
import { Link } from './ui/link';
import { Download, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { DESK_PRIMARY_NAV_ITEMS } from '../utils/desk-primary-nav';
import { cn } from '../lib/utils';
import {
  SIDEBAR_ACTION_GROUP_CLASSNAME,
  SIDEBAR_ACTION_HOVER_GROUP,
  SIDEBAR_ACTION_ICON_HOVER_CLASSNAME,
  SIDEBAR_ACTION_LABEL_HOVER_CLASSNAME,
} from '../utils/sidebar-action-hover';
import { UserAvatarMenu } from './UserAvatarMenu';
import { useLocation } from 'react-router-dom';
import { OfflineIndicator } from './OfflineIndicator';
import { ThemeToggle } from './ThemeToggle';
import { RingingBellIcon } from './RingingBellIcon';
import { RotatingSettingsIcon } from './RotatingSettingsIcon';
import { RotatingTrashIcon } from './RotatingTrashIcon';
import { ScalingHelpIcon } from './ScalingHelpIcon';
import { useTheme } from '../contexts/ThemeContext';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import { APP_SIDEBAR_HEADER_CLASSNAME, APP_SIDEBAR_HEADER_STYLE } from '../constants/layout';
import { EditorHelpDialog } from './EditorHelpDialog';
import { CountBadge } from './CountBadge';
import { useUserNotesCount } from '../hooks/useUserNotesCount';
import logoUrl from '../assets/logos/SVG/POSITIVO/logo scribane_positivo reducido h adjusted.svg?url';
import logoNegativoUrl from '../assets/logos/SVG/BLANCO/logo scribane_blanco reducido h adjusted.svg?url';

export const DeskSidebar: React.FC = () => {
  const { t } = useLocale();
  const location = useLocation();
  const { resolvedTheme } = useTheme();
  const { unreadCount } = useNotifications();
  const unassignedNotesCount = useUserNotesCount();
  const { fetchSettings } = useSettings();
  const { canInstall, isInstalled, promptInstall } = useInstallPrompt();
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  const { state, isMobile, openMobile, setOpenMobile, toggleSidebar } = useSidebar();
  const isCollapsed = isMobile ? !openMobile : state === 'collapsed';

  const handleMenuClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const handleSettingsClick = async () => {
    handleMenuClick();
    await fetchSettings();
  };

  return (
    <Sidebar collapsible="icon" className="bg-sidebar text-sidebar-foreground">
      <SidebarHeader className={APP_SIDEBAR_HEADER_CLASSNAME} style={APP_SIDEBAR_HEADER_STYLE}>
        <SidebarMenu className="h-full">
          <SidebarMenuItem className={cn('h-full', isCollapsed && 'flex items-center justify-center')}>
            {isCollapsed ? (
              <SidebarMenuButton
                tooltip="Toggle sidebar"
                className="h-[38px] !justify-center !text-center"
                onClick={toggleSidebar}
                aria-label="Toggle Sidebar"
              >
                <PanelLeftOpen className="!h-6 !w-6 text-primary" />
                <span className="sr-only">{t('toggle_sidebar')}</span>
              </SidebarMenuButton>
            ) : (
              <div className="flex items-center gap-1 w-full h-full">
                <SidebarMenuButton asChild tooltip="Scribane" className="h-full flex-1 py-0 pl-2">
                  <Link to="/" onClick={handleMenuClick} className="flex items-center w-full h-full py-0">
                    <img src={resolvedTheme === 'dark' ? logoNegativoUrl : logoUrl} alt="Scribane" className="h-[1.8rem] w-auto min-w-[150px]" />
                  </Link>
                </SidebarMenuButton>
                <SidebarMenuButton
                  tooltip="Toggle sidebar"
                  className="h-full !justify-center !text-center !p-1"
                  onClick={toggleSidebar}
                  aria-label="Toggle Sidebar"
                >
                  <PanelLeftClose className="!h-6 !w-6 text-primary" />
                  <span className="sr-only">{t('toggle_sidebar')}</span>
                </SidebarMenuButton>
              </div>
            )}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu className="gap-0.5">
              {DESK_PRIMARY_NAV_ITEMS.map((item) => {
                const label = t(item.labelKey) || item.fallbackLabel;
                const Icon = item.icon;
                const active = item.isActive(location.pathname);
                const showUnassignedNotesBadge = item.to === '/desk/notes';
                return (
                  <SidebarMenuItem key={item.to}>
                    <SidebarMenuButton
                      asChild
                      tooltip={label}
                      isActive={active}
                      className={cn('h-[38px] py-0', isCollapsed && '!justify-center !text-center !gap-0')}
                    >
                      <Link
                        to={item.to}
                        onClick={handleMenuClick}
                        className={cn(
                          isCollapsed && 'flex items-center justify-center w-full h-full relative',
                        )}
                      >
                        {isCollapsed ? (
                          <>
                            <Icon className="!h-6 !w-6 text-primary" />
                            {showUnassignedNotesBadge ? (
                              <CountBadge count={unassignedNotesCount} mode="overlay" maxDisplay={9} />
                            ) : null}
                          </>
                        ) : (
                          <>
                            <Icon className="!h-4 !w-4 text-secondary-foreground" />
                            <span className="text-primary">{label}</span>
                            {showUnassignedNotesBadge ? (
                              <CountBadge count={unassignedNotesCount} mode="inline" maxDisplay={9} />
                            ) : null}
                          </>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup className="mt-auto">
          <SidebarGroupContent>
            <SidebarMenu className="gap-0.5">
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  tooltip={t('notifications')}
                  className={cn(
                    SIDEBAR_ACTION_GROUP_CLASSNAME,
                    'h-[38px] py-0',
                    isCollapsed && '!justify-center !text-center !gap-0',
                  )}
                >
                  <Link to="/desk/notifications" onClick={handleMenuClick} className={cn(isCollapsed && 'flex items-center justify-center w-full h-full relative')}>
                    <RingingBellIcon
                      className={cn(
                        isCollapsed ? '!h-6 !w-6 text-primary' : '!h-4 !w-4 text-secondary-foreground',
                        SIDEBAR_ACTION_ICON_HOVER_CLASSNAME,
                      )}
                    />
                    {!isCollapsed && (
                      <span className={cn('text-primary', SIDEBAR_ACTION_LABEL_HOVER_CLASSNAME)}>
                        {t('notifications')}
                      </span>
                    )}
                    <CountBadge
                      count={unreadCount}
                      mode={isCollapsed ? 'overlay' : 'inline'}
                      maxDisplay={9}
                    />
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {!isCollapsed && !isMobile && (
                <SidebarMenuItem>
                  <OfflineIndicator variant="sidebar" isCollapsed={isCollapsed} />
                </SidebarMenuItem>
              )}
              <SidebarMenuItem>
                <ThemeToggle collapsed={isCollapsed} />
              </SidebarMenuItem>
              {canInstall && !isInstalled && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip={t('pwa_install_app')}
                    className={cn('h-[38px] py-0', isCollapsed && '!justify-center !text-center !gap-0')}
                    onClick={() => {
                      handleMenuClick();
                      void promptInstall();
                    }}
                  >
                    {isCollapsed ? (
                      <Download className="!h-6 !w-6 text-primary" />
                    ) : (
                      <>
                        <Download className="!h-4 !w-4 text-secondary-foreground" />
                        <span className="text-primary">{t('pwa_install_app')}</span>
                      </>
                    )}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  tooltip={t('user_settings') || 'User settings'}
                  className={cn(
                    SIDEBAR_ACTION_GROUP_CLASSNAME,
                    'h-[38px] py-0',
                    isCollapsed && '!justify-center !text-center !gap-0',
                  )}
                >
                  <Link to="/desk/settings" onClick={handleSettingsClick} className={cn(isCollapsed && 'flex items-center justify-center w-full h-full')}>
                    <RotatingSettingsIcon
                      className={cn(
                        isCollapsed ? '!h-6 !w-6 text-primary' : '!h-4 !w-4 text-secondary-foreground',
                        SIDEBAR_ACTION_ICON_HOVER_CLASSNAME,
                      )}
                    />
                    {!isCollapsed && (
                      <span className={cn('text-primary', SIDEBAR_ACTION_LABEL_HOVER_CLASSNAME)}>
                        {t('settings')}
                      </span>
                    )}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  tooltip={t('trash')}
                  className={cn(
                    SIDEBAR_ACTION_GROUP_CLASSNAME,
                    'h-[38px] py-0',
                    isCollapsed && '!justify-center !text-center !gap-0',
                  )}
                >
                  <Link to="/desk/trash" onClick={handleMenuClick} className={cn(isCollapsed && 'flex items-center justify-center w-full h-full')}>
                    <RotatingTrashIcon
                      hoverGroup={SIDEBAR_ACTION_HOVER_GROUP}
                      className={cn(
                        isCollapsed ? '!h-6 !w-6 text-primary' : '!h-4 !w-4 text-secondary-foreground',
                        SIDEBAR_ACTION_ICON_HOVER_CLASSNAME,
                      )}
                    />
                    {!isCollapsed && (
                      <span className={cn('text-primary', SIDEBAR_ACTION_LABEL_HOVER_CLASSNAME)}>
                        {t('trash')}
                      </span>
                    )}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip={t('help')}
                  className={cn(
                    SIDEBAR_ACTION_GROUP_CLASSNAME,
                    'h-[38px] py-0',
                    isCollapsed && '!justify-center !text-center !gap-0',
                  )}
                  onClick={() => {
                    handleMenuClick();
                    setHelpDialogOpen(true);
                  }}
                >
                  <ScalingHelpIcon
                    className={cn(
                      isCollapsed ? '!h-6 !w-6 text-primary' : '!h-4 !w-4 text-secondary-foreground',
                      SIDEBAR_ACTION_ICON_HOVER_CLASSNAME,
                    )}
                  />
                  {!isCollapsed && (
                    <span className={cn('text-primary', SIDEBAR_ACTION_LABEL_HOVER_CLASSNAME)}>
                      {t('help')}
                    </span>
                  )}
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <UserAvatarMenu />
      <EditorHelpDialog open={helpDialogOpen} onOpenChange={setHelpDialogOpen} />
    </Sidebar>
  );
};

